/**
 * The four land Playbook templates a company starts from, and the Popety default applied
 * when a company has published no land method. Written in French; the Brief is generated
 * in the reader's language regardless.
 */
import type {
  LandPlaybook,
  LandPlaybookTemplate,
  LandTemplateId,
  PlaybookProfile,
} from '@popety-io/types'

const profile = (over: Partial<PlaybookProfile>): PlaybookProfile => ({
  company_type: 'promoteur',
  segment: 'standard',
  brand: null,
  language: 'fr',
  address_form: 'vous',
  ...over,
})

export const LAND_BRIEF_DEFAULT_PLAYBOOK: LandPlaybook = {
  name: 'Méthode Popety par défaut',
  profile: profile({ company_type: 'agence' }),
  q1_target: {
    products: ['ppe_neuve', 'locatif', 'mixte'],
    min_threshold: 'none',
  },
  q2_killers: {
    ids: ['bati_protege', 'ppe', 'danger_naturel', 'proprietaire_public'],
  },
  q3_checks: {
    always: ['extrait_rf', 'reglement_zone', 'rdppf', 'plan_quartier'],
    custom: [],
  },
  q4_indices: { alg: 'show', cite_study: true },
  q5_style: { tone: 'sobre', length: 'standard' },
  advanced: {},
}

const promoteur: LandPlaybook = {
  name: 'Promoteur / développeur',
  profile: profile({ company_type: 'promoteur' }),
  q1_target: {
    products: ['ppe_neuve', 'locatif'],
    min_threshold: 'floor_1500',
  },
  q2_killers: {
    ids: ['bati_protege', 'ppe', 'danger_naturel', 'proprietaire_public'],
  },
  q3_checks: {
    always: ['extrait_rf', 'reglement_zone', 'rdppf', 'plan_quartier'],
    custom: [],
  },
  q4_indices: { alg: 'show', cite_study: true },
  q5_style: { tone: 'sobre', length: 'standard' },
  advanced: {
    process: [
      'Extrait RF et RDPPF commandés',
      "Esquisse d'implantation par l'architecte",
      'Étude de faisabilité Popety avec coûts',
      "Comité d'acquisition",
    ],
    purchase_conditions: { type: 'promesse_conditionnee', delay_months: 12 },
    notes: null,
  },
}

const agence: LandPlaybook = {
  name: 'Courtier en acquisition',
  profile: profile({ company_type: 'agence' }),
  q1_target: {
    products: ['ppe_neuve', 'villas', 'revente_permis'],
    min_threshold: 'none',
  },
  q2_killers: { ids: ['bati_protege', 'proprietaire_public'] },
  q3_checks: {
    always: ['extrait_rf', 'reglement_zone', 'permis_voisins', 'viabilisation'],
    custom: [],
  },
  q4_indices: { alg: 'reg_only', cite_study: true },
  q5_style: { tone: 'direct', length: 'court' },
  advanced: {
    process: [
      'Prise de contact avec le propriétaire',
      'Dossier foncier et permis',
      'Mise en relation avec un acquéreur',
    ],
    purchase_conditions: { type: null, delay_months: null },
    notes: null,
  },
}

const banque: LandPlaybook = {
  name: 'Banque, fiduciaire, expert',
  profile: profile({ company_type: 'banque', segment: 'rendement' }),
  q1_target: { products: ['locatif', 'mixte'], min_threshold: 'units_10' },
  q2_killers: { ids: ['bati_protege', 'danger_naturel', 'protection_eaux'] },
  q3_checks: {
    always: ['extrait_rf', 'rdppf', 'loyers', 'pollution_sols'],
    custom: [],
  },
  q4_indices: { alg: 'alg_if_none', cite_study: true },
  q5_style: { tone: 'technique', length: 'detaille' },
  advanced: {
    process: [
      'Extrait RF et état locatif',
      'Expertise de la valeur du terrain',
      'Décision de crédit',
    ],
    purchase_conditions: { type: null, delay_months: null },
    notes: null,
  },
}

const regie: LandPlaybook = {
  name: 'Régie / gérance',
  profile: profile({ company_type: 'regie', segment: 'rendement' }),
  q1_target: { products: ['surelevation', 'locatif'], min_threshold: 'none' },
  q2_killers: { ids: ['bati_protege', 'ppe', 'danger_naturel'] },
  q3_checks: {
    always: ['extrait_rf', 'reglement_zone', 'loyers', 'plan_quartier'],
    custom: [],
  },
  q4_indices: { alg: 'show', cite_study: true },
  q5_style: { tone: 'sobre', length: 'standard' },
  advanced: {
    process: [
      'État locatif et baux en cours',
      'Contact avec le propriétaire mandant',
      'Étude de surélévation avec un architecte',
    ],
    purchase_conditions: { type: null, delay_months: null },
    notes: null,
  },
}

export const LAND_PLAYBOOK_TEMPLATES: Record<LandTemplateId, LandPlaybook> = {
  promoteur,
  agence,
  banque,
  regie,
}

export const listLandPlaybookTemplates = (): LandPlaybookTemplate[] =>
  (Object.keys(LAND_PLAYBOOK_TEMPLATES) as LandTemplateId[]).map((id) => ({
    id,
    playbook: LAND_PLAYBOOK_TEMPLATES[id],
  }))
